/**
 * Folder path breadcrumb resolution utility.
 *
 * Extracts the logic for resolving document → project → folder → filename
 * breadcrumb paths so it can be shared between List view and Timeline view.
 */

export interface FolderPathSegment {
  label: string;
  type: "project" | "folder" | "filename";
  /** Project ID for navigation (present on project and folder segments) */
  projectId?: string;
  /** Folder ID for navigation */
  folderId?: string;
}

interface ProjectLike {
  name: string;
}

export interface FolderLike {
  folder_path?: string | null;
  name?: string;
  project_id?: number | null;
  parentId?: string | null;
}

/**
 * Walk up the folder tree from a leaf folder to build an ordered list of
 * ancestor folder IDs (root-first).
 */
function collectFolderAncestorIds(
  leafFolderId: string,
  folders: Map<string, FolderLike>
): string[] {
  const ids: string[] = [];
  let current = folders.get(leafFolderId);
  let currentId: string | null = leafFolderId;

  while (current && currentId) {
    ids.unshift(currentId);
    currentId = current.parentId ?? null;
    current = currentId ? folders.get(currentId) : undefined;
  }

  return ids;
}

/**
 * Resolves the breadcrumb segments for a document.
 * Returns null if only the filename exists (no project/folder context).
 *
 * Each folder segment gets the correct folder ID by walking the parent chain,
 * so clicking any segment navigates to that specific folder.
 */
export function resolveDocumentBreadcrumb(
  doc: { project_id: number | null; folder_id: string | number | null; filename: string },
  projects: Map<string, ProjectLike>,
  folders: Map<string, FolderLike>
): FolderPathSegment[] | null {
  const segments: FolderPathSegment[] = [];

  const projectId = doc.project_id ? String(doc.project_id) : undefined;

  if (projectId) {
    const project = projects.get(projectId);
    if (project?.name) {
      segments.push({ label: project.name, type: "project", projectId });
    }
  }

  if (doc.folder_id) {
    const leafFolderId = String(doc.folder_id);
    const folder = folders.get(leafFolderId);
    const folderProjectId =
      projectId ?? (folder?.project_id ? String(folder.project_id) : undefined);

    // Walk up the tree so each segment carries its own folder's id and name.
    const ancestorIds = collectFolderAncestorIds(leafFolderId, folders);
    const pathParts = (folder?.folder_path ?? "").split("/").filter((p: string) => p.trim());

    // The walk is only trustworthy if it reached a root. A chain broken by a
    // folder missing from the map (e.g. hidden ancestors are filtered out of
    // some folder queries) would render a truncated breadcrumb, so prefer the
    // stored path when it covers more of the chain than we collected.
    const chainReachesRoot =
      ancestorIds.length > 0 && folders.get(ancestorIds[0])?.parentId == null;

    if (folder?.name && (chainReachesRoot || pathParts.length <= ancestorIds.length)) {
      // A folder's own `name` is its label. `folder_path` only fills in an
      // ancestor that predates the `name` column, and never overrides a name we
      // hold: the DB keeps the path one rename behind (its trigger recomputes
      // from the pre-update row), so a path-derived label can be a stale name.
      // pathParts is root→leaf like ancestorIds, so align them from the end.
      const offset = pathParts.length - ancestorIds.length;
      ancestorIds.forEach((id, index) => {
        // A stored path can still be shorter than the real chain — a row
        // written before the path cascade landed (franklin-data-pipeline
        // `refresh_descendant_folder_paths` re-paths a whole subtree on a
        // parent change), or one this page read before a move. A name-less
        // ancestor may then have no aligned path part either — keep the level
        // with a placeholder rather than render a shallower hierarchy.
        const label = folders.get(id)?.name || pathParts[offset + index] || "…";
        segments.push({
          label,
          type: "folder",
          folderId: id,
          projectId: folderProjectId,
        });
      });
    } else if (pathParts.length > 0) {
      // Legacy leaf without a `name`, or a broken chain: expand the stored
      // path. ancestorIds is the tail of the chain, so align ids from the
      // end; prefix parts we hold no folder for get no id (not clickable).
      const offset = pathParts.length - ancestorIds.length;
      pathParts.forEach((part: string, index: number) => {
        segments.push({
          label: part,
          type: "folder",
          folderId: ancestorIds[index - offset],
          projectId: folderProjectId,
        });
      });
    } else {
      // The document names a folder that neither the folder map nor a stored
      // path can label — it belongs to another project, was soft-deleted, or is
      // otherwise outside the set this surface loaded. Emitting nothing here
      // rendered it as "Project / filename", which is exactly how a file at the
      // project ROOT reads: a misfiled document then looked correctly filed,
      // and the Folders tab (which can show neither it nor its folder) looked
      // like it was losing root files. Keep the level with a placeholder, and
      // no id AT ALL — not even the project's. BreadcrumbTooltipContent makes a
      // segment clickable on `projectId` alone, and that click navigates to the
      // project ROOT: the placeholder would offer to open the one folder this
      // document is provably not in.
      //
      // Only once something real has been resolved, though: a placeholder on
      // its own would make `segments.length > 1` true below and return a
      // breadcrumb reading "… / filename" — all placeholder, no context —
      // where a caller expects the null that means "no breadcrumb".
      if (segments.length > 0) {
        segments.push({ label: "…", type: "folder" });
      }
    }
  }

  segments.push({ label: doc.filename, type: "filename" });

  return segments.length > 1 ? segments : null;
}

/**
 * Flat string version — "Project Name / Folder / Subfolder / filename.ext"
 * Used by DocumentListTable's existing documentFolderPaths map.
 */
export function resolveDocumentFolderPath(
  doc: { project_id: number | null; folder_id: string | number | null; filename: string },
  projects: Map<string, ProjectLike>,
  folders: Map<string, FolderLike>
): string | undefined {
  const segments = resolveDocumentBreadcrumb(doc, projects, folders);
  if (!segments) return undefined;
  return segments.map((s) => s.label).join(" / ");
}
